import {useMemo, useState} from "react";
import {Button, Stack, Typography} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import {appStyles} from "../../../../AppStyles.ts";
import {StyledInputBase, StyledSearch, StyledSearchIconWrapper} from "../../../AppStyledComponents.tsx";
import type {MediaLink} from "../../../../sdo/MediaLink.ts";
import MediaCard from "./media/MediaCard.tsx";

let nextId = 100;

function createEmptyMedia(): MediaLink {
    return {
        uid: String(nextId++),
        mediaType: "IMAGE",
        sourceURL: "",
        caption: "",
        details: "",
        location: "",
        latitude: 0,
        longitude: 0,
        datetime: 0,
    };
}

// TODO: Replace with API call to load media from IntelProfileManager.readMedia()
function buildTestData(): MediaLink[] {
    return [
        {
            uid: "1",
            mediaType: "IMAGE",
            sourceURL: "https://example.com/media/profile-photo-2026-03.jpg",
            caption: "Surveillance photograph captured during the March observation window.",
            details: "Image retrieved from partner network feed; resolution sufficient for facial comparison.",
            location: "San Francisco, CA",
            latitude: 37.7749,
            longitude: -122.4194,
            datetime: new Date("2026-03-12T14:32:00").getTime(),
        },
        {
            uid: "2",
            mediaType: "VIDEO",
            sourceURL: "https://example.com/media/lobby-cam-clip-001.mp4",
            caption: "Lobby camera footage showing subject entering the building.",
            details: "Original source resolution preserved. Timestamps verified against camera metadata.",
            location: "Downtown Office Tower",
            latitude: 37.7891,
            longitude: -122.4012,
            datetime: new Date("2026-03-15T09:14:00").getTime(),
        },
        {
            uid: "3",
            mediaType: "AUDIO",
            sourceURL: "https://example.com/media/voicemail-recording-04.wav",
            caption: "Voicemail recording referenced in the analyst notes.",
            details: "Audio enhancement applied; transcript available in linked resource.",
            location: "",
            latitude: 0,
            longitude: 0,
            datetime: new Date("2026-02-28T17:48:00").getTime(),
        },
        {
            uid: "4",
            mediaType: "IMAGE",
            sourceURL: "https://example.com/media/document-scan-007.png",
            caption: "Scanned document page referenced in supporting resources.",
            details: "",
            location: "",
            latitude: 0,
            longitude: 0,
            datetime: 0,
        },
    ];
}

function UXIntelImagesPanel() {
    const [mediaList, setMediaList] = useState<MediaLink[]>(buildTestData);
    const [filterText, setFilterText] = useState("");
    const [newMediaUid, setNewMediaUid] = useState<string | null>(null);

    const filtered = useMemo(() => {
        if (!filterText.trim()) return mediaList;
        const q = filterText.toLowerCase();
        return mediaList.filter((m) => JSON.stringify(m).toLowerCase().includes(q));
    }, [mediaList, filterText]);

    const handleDelete = (uid: string) => {
        setMediaList((prev) => prev.filter((m) => m.uid !== uid));
        if (newMediaUid === uid) setNewMediaUid(null);
    };

    const handleUpdate = (updated: MediaLink) => {
        setMediaList((prev) => prev.map((m) => m.uid === updated.uid ? updated : m));
    };

    const handleImport = () => {
        const newMedia = createEmptyMedia();
        setMediaList((prev) => [newMedia, ...prev]);
        setNewMediaUid(newMedia.uid);
    };

    return (
        <Stack sx={appStyles.defaultPanelContainer} direction="column" padding={1} width="100%">
            {/* Title */}
            <Stack direction="row" padding={1} justifyContent="center">
                <Typography sx={appStyles.panelSectionTitle}>Media</Typography>
            </Stack>

            {/* Filter bar */}
            <Stack direction="row" spacing={1} padding={1} alignItems="center" justifyContent="center" sx={{position: "relative"}}>
                <StyledSearch>
                    <StyledSearchIconWrapper>
                        <SearchIcon sx={{fontSize: 16, color: "#7093a0"}}/>
                    </StyledSearchIconWrapper>
                    <StyledInputBase
                        placeholder="Filter media..."
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </StyledSearch>

                <Typography sx={appStyles.panelFilterCount}>
                    Showing {filtered.length} of {mediaList.length}
                </Typography>

                <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon/>}
                    onClick={handleImport}
                    sx={{...appStyles.panelActionButton, position: "absolute", right: 8}}
                >
                    Import Media
                </Button>
            </Stack>

            {/* Media cards list */}
            <Stack direction="column" spacing={1} sx={{...appStyles.defaultPanelCardsWrapper, padding: "8px", alignItems: "stretch"}}>
                {filtered.map((media) => (
                    <MediaCard
                        key={media.uid}
                        media={media}
                        onDelete={handleDelete}
                        onUpdate={handleUpdate}
                        startInEditMode={media.uid === newMediaUid}
                    />
                ))}
                {filtered.length === 0 && (
                    <Typography sx={{
                        color: "rgba(242, 245, 250, 0.4)",
                        fontSize: "0.80rem",
                        textAlign: "center",
                        padding: "24px",
                    }}>
                        No media entries match the current filter.
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
}

export default UXIntelImagesPanel;
